import axios from 'axios';
import { action, computed, makeObservable, reaction } from 'mobx';

import { AlertsStore } from '@/configs/store/AlertsStore';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { ValueModel } from '@/models/ValueModel';
import { trackerApi } from '@/tracker/api/trackerApi';
import { CLOSED_STATUS_TYPES } from '@/tracker/entities/status';
import { DEFAULT_SORT, IssueSort } from '@/tracker/features/issueList';
import { TaskFiltersModel } from '@/tracker/features/taskFilters';
import { TrackerStore } from '@/tracker/model';
import { Queue, TaskBasic, TaskFilters } from '@/tracker/types';
import { showError } from '@/tracker/utils/showError';

const PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 300;

/** Задачи очереди с быстрыми фильтрами, подгрузка страниц по курсору */
export class QueueStore extends LocalStore {
  private readonly _alertsStore: AlertsStore;
  private readonly _trackerStore: TrackerStore;
  private readonly _queueKey: string;
  private readonly _queue = new ValueModel<Queue | null>(null);
  private readonly _tasks = new ValueModel<TaskBasic[]>([]);
  private readonly _showClosed = new ValueModel(false);
  /** Плашки фильтров: статус, тип, приоритет, люди, спринт */
  readonly filters = new TaskFiltersModel();
  private readonly _query = new ValueModel('');
  private readonly _sort = new ValueModel<IssueSort>(DEFAULT_SORT);
  private readonly _nextPageToken = new ValueModel<string | null>(null);
  private readonly _totalSize = new ValueModel(0);
  private readonly _isNotFound = new ValueModel(false);
  private readonly _loadingStage = new LoadingStageModel();
  private readonly _tasksStage = new LoadingStageModel();
  /** Ответ на устаревшие фильтры не должен перетереть свежий */
  private _request = 0;

  constructor({
    alertsStore,
    trackerStore,
    queueKey,
  }: {
    alertsStore: AlertsStore;
    trackerStore: TrackerStore;
    queueKey: string;
  }) {
    super();
    this._alertsStore = alertsStore;
    this._trackerStore = trackerStore;
    this._queueKey = queueKey;

    makeObservable(this, {
      queue: computed,
      tasks: computed,
      showClosed: computed,
      query: computed,
      sort: computed,
      totalSize: computed,
      hasMore: computed,
      isNotFound: computed,
      isError: computed,
      isTasksLoading: computed,
      isTasksLoaded: computed,
      isTasksError: computed,
      load: action.bound,
      loadTasks: action.bound,
      loadMore: action.bound,
      setShowClosed: action.bound,
      setQuery: action.bound,
      setSort: action.bound,
    });
  }

  get queue() {
    return this._queue.value;
  }

  get tasks() {
    return this._tasks.value;
  }

  get showClosed() {
    return this._showClosed.value;
  }

  get query() {
    return this._query.value;
  }

  get sort() {
    return this._sort.value;
  }

  get totalSize() {
    return this._totalSize.value;
  }

  get hasMore() {
    return this._nextPageToken.value !== null;
  }

  get isNotFound() {
    return this._isNotFound.value;
  }

  get isError() {
    return this._loadingStage.isError;
  }

  get isTasksLoading() {
    return this._tasksStage.isLoading;
  }

  /** Последний запрос задач успешен — можно показывать «задач нет» */
  get isTasksLoaded() {
    return this._tasksStage.isSuccess;
  }

  get isTasksError() {
    return this._tasksStage.isError;
  }

  setShowClosed(value: boolean) {
    this._showClosed.change(value);
  }

  setQuery(query: string) {
    this._query.change(query);
    // Курсор привязан к query: со старым «Показать ещё» до перезагрузки списка дало бы 400
    this._nextPageToken.change(null);
  }

  setSort(sort: IssueSort) {
    this._sort.change(sort);
  }

  async load() {
    try {
      this._loadingStage.loading();

      const queue = await trackerApi.getQueue(this._queueKey);

      this._queue.change(queue);
      this._trackerStore.addRecent({ type: 'queue', id: queue.key, name: queue.name });
      this._loadingStage.success();
    } catch (error) {
      this._loadingStage.error();

      // 403 отдаётся независимо от существования очереди — для пользователя это «не найдена»
      if (axios.isAxiosError(error) && [403, 404].includes(error.response?.status ?? 0)) {
        this._isNotFound.change(true);

        return;
      }

      showError({ alertsStore: this._alertsStore, title: 'Не удалось загрузить очередь', error });

      return;
    }

    this.addReactions([
      reaction(
        () => [this._showClosed.value, this.filters.values, this._sort.value],
        () => this.loadTasks(),
        { fireImmediately: true },
      ),
      reaction(
        () => this._query.value,
        () => this.loadTasks(),
        { delay: SEARCH_DELAY_MS },
      ),
      // Задачу поменяли в карточке — обновляем строку без перезагрузки списка
      reaction(
        () => this._trackerStore.updatedIssue,
        (updated) => {
          if (!updated) return;

          this._tasks.change(
            this._tasks.value.map((task) => (task.key === updated.key ? updated : task)),
          );
        },
      ),
    ]);
  }

  /** Первая страница под текущие фильтры */
  loadTasks() {
    return this.loadPage(null);
  }

  loadMore() {
    return this.loadPage(this._nextPageToken.value);
  }

  private taskFilters(): TaskFilters {
    const filters: TaskFilters = { ...this.filters.toTaskFilters(), queue: { eq: this._queueKey } };

    // Выбранные статусы важнее «Показывать закрытые»: иначе выбор закрытого статуса давал бы пустой список
    if (!filters.status && !this._showClosed.value) {
      filters.statusType = { notIn: CLOSED_STATUS_TYPES };
    }

    return filters;
  }

  private async loadPage(pageToken: string | null) {
    const request = ++this._request;

    try {
      this._tasksStage.loading();

      const page = await trackerApi.searchTasks({
        query: this._query.value.trim() || undefined,
        filters: this.taskFilters(),
        orderBy: [this._sort.value],
        pageSize: PAGE_SIZE,
        pageToken: pageToken ?? undefined,
      });

      if (request !== this._request) return;

      this._tasks.change(pageToken ? [...this._tasks.value, ...page.items] : page.items);
      this._nextPageToken.change(page.nextPageToken);
      this._totalSize.change(page.totalSize);
      this._tasksStage.success();
    } catch (error) {
      if (request !== this._request) return;

      this._tasksStage.error();
      showError({
        alertsStore: this._alertsStore,
        title: 'Не удалось загрузить задачи очереди',
        error,
      });
    }
  }
}
